/**
 * Client for engine /v1/erp shop-floor routes (ERP v2-1):
 * machines, work order operations, time logs, real cost, estimate accuracy.
 *
 * Engine gates these behind SEMANTICAD_ERP_V2 and answers 404 `erp_v2_disabled`
 * when it is off — callers should treat that as "not enabled", not an error.
 */
import { jsonFetch, ErpApiError } from "./erpApi";

export type OperationStatus = "pending" | "running" | "done" | "skipped";

export interface TimeLog {
  id?: string;
  operation_id?: string;
  user_id?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  hours?: number | null;
  qty_good?: number | null;
  qty_scrap?: number | null;
  note?: string | null;
}

export interface WorkOrderOperation {
  id?: string;
  work_order_id?: string;
  op_no: number;
  op_code: string;
  label_tr?: string | null;
  machine_id?: string | null;
  machine_code?: string | null;
  machine_name?: string | null;
  hourly_rate_try: number;
  est_setup_hrs: number;
  est_run_hrs: number;
  actual_hours: number;
  qty_good: number;
  qty_scrap: number;
  status: OperationStatus;
  logs?: TimeLog[];
}

export interface Machine {
  id?: string;
  code: string;
  name: string;
  kind?: string | null;
  hourly_rate_try: number;
  capacity_hours_per_day: number;
  status: "active" | "inactive";
}

export interface WorkOrderMaterial {
  id?: number;
  work_order_id?: string;
  stock_item_id?: number | null;
  part_no?: string | null;
  qty_required: number;
  qty_issued: number;
  unit_cost_try: number;
}

export interface WorkOrderCost {
  work_order_id?: string;
  material_cost_try: number;
  labor_cost_try: number;
  total_cost_try: number;
  est_hours: number;
  actual_hours: number;
  hours_variance: number;
  qty_good: number;
  qty_scrap: number;
  scrap_rate: number;
  cost_per_good_unit_try?: number | null;
  est_cost_try?: number | null;
  cost_variance_try?: number | null;
}

export interface EstimateAccuracyRow {
  op_code: string;
  operation_count: number;
  est_hours: number;
  actual_hours: number;
  variance_hours: number;
  ratio?: number | null;
  qty_good: number;
  qty_scrap: number;
  scrap_rate: number;
}

/** True when the engine reports the v2 feature flag is off. */
export function isErpV2Disabled(e: unknown): boolean {
  return (
    e instanceof ErpApiError &&
    e.status === 404 &&
    (e.code === "erp_v2_disabled" || e.message.includes("erp_v2_disabled"))
  );
}

// --- machines -------------------------------------------------------------

export function fetchMachines(includeInactive = false): Promise<{
  items: Machine[];
  count: number;
}> {
  const q = includeInactive ? "?include_inactive=true" : "";
  return jsonFetch(`/v1/erp/machines${q}`);
}

export interface MachineCreateBody {
  code: string;
  name: string;
  kind?: string | null;
  hourly_rate_try?: number;
  capacity_hours_per_day?: number;
}

export function createMachine(body: MachineCreateBody): Promise<Machine> {
  return jsonFetch(`/v1/erp/machines`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateMachine(
  machineId: string,
  body: Partial<MachineCreateBody> & { status?: "active" | "inactive" },
): Promise<Machine> {
  return jsonFetch(`/v1/erp/machines/${encodeURIComponent(machineId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// --- operations -----------------------------------------------------------

export function fetchOperations(workOrderId: string): Promise<{
  items: WorkOrderOperation[];
  count: number;
}> {
  return jsonFetch(`/v1/erp/work-orders/${encodeURIComponent(workOrderId)}/operations`);
}

export function addOperation(
  workOrderId: string,
  body: {
    op_code: string;
    label_tr?: string | null;
    machine_id?: string | null;
    est_setup_hrs?: number;
    est_run_hrs?: number;
  },
): Promise<WorkOrderOperation> {
  return jsonFetch(`/v1/erp/work-orders/${encodeURIComponent(workOrderId)}/operations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateOperation(
  operationId: string,
  body: {
    machine_id?: string | null;
    est_setup_hrs?: number;
    est_run_hrs?: number;
    label_tr?: string | null;
    status?: OperationStatus;
  },
): Promise<WorkOrderOperation> {
  return jsonFetch(`/v1/erp/operations/${encodeURIComponent(operationId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function startOperation(operationId: string): Promise<WorkOrderOperation> {
  return jsonFetch(`/v1/erp/operations/${encodeURIComponent(operationId)}/start`, {
    method: "POST",
  });
}

export function finishOperation(
  operationId: string,
  body: { qty_good?: number; qty_scrap?: number; hours?: number; note?: string },
): Promise<WorkOrderOperation> {
  return jsonFetch(`/v1/erp/operations/${encodeURIComponent(operationId)}/finish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// --- materials ------------------------------------------------------------

export function fetchWoMaterials(workOrderId: string): Promise<{
  items: WorkOrderMaterial[];
  count: number;
}> {
  return jsonFetch(`/v1/erp/work-orders/${encodeURIComponent(workOrderId)}/materials`);
}

export function addWoMaterial(
  workOrderId: string,
  body: { stock_item_id?: number; part_no?: string; qty_required?: number },
): Promise<WorkOrderMaterial> {
  return jsonFetch(`/v1/erp/work-orders/${encodeURIComponent(workOrderId)}/materials`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function issueWoMaterial(
  workOrderId: string,
  materialId: number,
  qty: number,
): Promise<WorkOrderMaterial> {
  return jsonFetch(
    `/v1/erp/work-orders/${encodeURIComponent(workOrderId)}/materials/${materialId}/issue`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ qty }),
    },
  );
}

// --- cost / analytics -----------------------------------------------------

export function fetchWorkOrderCost(workOrderId: string): Promise<WorkOrderCost> {
  return jsonFetch(`/v1/erp/work-orders/${encodeURIComponent(workOrderId)}/cost`);
}

export function closeWorkOrder(workOrderId: string): Promise<WorkOrderCost> {
  return jsonFetch(`/v1/erp/work-orders/${encodeURIComponent(workOrderId)}/close`, {
    method: "POST",
  });
}

export function fetchEstimateAccuracy(opCode?: string): Promise<{
  items: EstimateAccuracyRow[];
  count: number;
}> {
  const q = opCode ? `?op_code=${encodeURIComponent(opCode)}` : "";
  return jsonFetch(`/v1/erp/analytics/estimate-accuracy${q}`);
}
